import { lighten, mix, rgba, saturate } from "polished";
import styled, { css } from "styled-components";

import { Theme } from "../../theme/common/theme";

export const fgShades = {
  "100": 0,
  "200": 0.026,
  "300": 0.056,
  "400": 0.077,
  "500": 0.103,
  "600": 0.129,
  "700": 0.172,
  "800": 0.249,
  "900": 0.386,
  "1000": 0.446,
  "1100": 0.592,
  "1200": 0.923,
};

export function alphaPalette({
  alpha,
  background,
  color,
}: {
  alpha: keyof typeof fgShades;
  color: string;
  background?: string;
}): string {
  return background
    ? mix(fgShades[alpha], color, background)
    : rgba(color, fgShades[alpha]);
}

export function primaryPalette(
  theme: Theme,
  alpha: keyof typeof fgShades,
  flatten: boolean = false,
): string {
  return alphaPalette({
    color: theme.intent.PRIMARY,
    alpha,
    background: flatten ? theme.backgroundColor.DEFAULT : undefined,
  });
}

export function organicShadow(
  args: {
    values?: number[];
    opacity?: number;
  } = {},
): string {
  const shadowDefaults = {
    opacity: 0.06,
    values: [1, 3, 6, 12, 24],
  };
  const _args = { ...shadowDefaults, ...args };
  const { opacity, values } = _args;
  return (
    values
      // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- how do I do this without violating this lint rule?
      .map((v) => `0 ${v}px ${v}px -${v * 0.5}px rgba(0 0 0 / ${opacity})`)
      // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization -- how do I do this without violating this lint rule?
      .join(", ")
  );
}

export const shadows = {
  s: organicShadow({ values: [1, 2, 4], opacity: 0.28 }),
  l: organicShadow({ values: [3, 6, 12, 24, 32], opacity: 0.08 }),
  xl: organicShadow({ values: [3, 12, 32, 80, 128], opacity: 0.16 }),
};

export const glassSurfaceCss = css`
  border-radius: 12px;

  background-image: radial-gradient(
    circle at 66% 420%,
    ${({ theme }) => lighten(0.043, primaryPalette(theme, "700", true))} 0%,
    ${({ theme }) => primaryPalette(theme, "200", true)} 80%
  );
  box-shadow: ${({ theme }) => {
    const innerShadow = `0 0 24px 0px ${primaryPalette(theme, "300")} inset`;
    const shadow = shadows.l;
    return [innerShadow, shadow].join(", ");
  }};

  &::after {
    pointer-events: none;
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    border: 1px solid transparent;
    opacity: 1;
    background-image: ${({ theme }) => {
      const stop1 = lighten(0.125, primaryPalette(theme, "700", true));
      const stop2 = primaryPalette(theme, "600", true);
      const stop3 = lighten(0.125 * 0.9, primaryPalette(theme, "700", true));

      return `
        radial-gradient(
          circle at 60% 330%,
          ${lighten(0.043, primaryPalette(theme, "900", true))} 0%,
          ${primaryPalette(theme, "100")} 80%
        ),
        linear-gradient(
          var(--shine-angle, 160deg),
          ${stop1} -30%,
          ${stop2} 140px,
          ${stop2} 60%,
          ${stop3} 140%
        )
      `;
    }};
    background-clip: border-box;
    background-origin: border-box;
    mask:
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px)
        padding-box exclude,
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
  }
`;

export function generateBorderGradientAnimCss({
  duration,
  gradientCenterPadding,
  gradientStops,
  height,
  width,
}: {
  duration: string;
  gradientCenterPadding: number;
  gradientStops: string[];
  height: number;
  width: number;
}) {
  function percentFromAspectRatio(x: number, y: number) {
    return x / (x + y);
  }

  const percentX = Math.floor(
    50 *
      percentFromAspectRatio(
        width - gradientCenterPadding,
        height - gradientCenterPadding,
      ),
  );

  const keyframes = {
    "0%": `
      --border-gradient-anim-cx: 0%;
      --border-gradient-anim-cy: 0%;
      --border-gradient-anim-cx-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-cy-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-angle: 0turn;
    `,
    [`${percentX}%`]: `
      --border-gradient-anim-cx: 100%;
      --border-gradient-anim-cy: 0%;
      --border-gradient-anim-cx-padding: -${gradientCenterPadding}px;
      --border-gradient-anim-cy-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-angle: 0.25turn;
    `,
    "50%": `
      --border-gradient-anim-cx: 100%;
      --border-gradient-anim-cy: 100%;
      --border-gradient-anim-cx-padding: -${gradientCenterPadding}px;
      --border-gradient-anim-cy-padding: -${gradientCenterPadding}px;
      --border-gradient-anim-angle: 0.5turn;
    `,
    [`${50 + percentX}%`]: `
      --border-gradient-anim-cx: 0%;
      --border-gradient-anim-cy: 100%;
      --border-gradient-anim-cx-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-cy-padding: -${gradientCenterPadding}px;
      --border-gradient-anim-angle: 0.75turn;
    `,
    "100%": `
      --border-gradient-anim-cx: 0%;
      --border-gradient-anim-cy: 0%;
      --border-gradient-anim-cx-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-cy-padding: ${gradientCenterPadding}px;
      --border-gradient-anim-angle: 1turn;
    `,
  };

  return css`
    @property --border-gradient-anim-angle {
      syntax: "<angle>";
      inherits: false;
      initial-value: 0turn;
    }
    @property --border-gradient-anim-cx {
      syntax: "<percentage>";
      inherits: false;
      initial-value: 0%;
    }
    @property --border-gradient-anim-cy {
      syntax: "<percentage>";
      inherits: false;
      initial-value: 0%;
    }
    @property --border-gradient-anim-cx-padding {
      syntax: "<length>";
      inherits: true;
      initial-value: ${gradientCenterPadding}px;
    }
    @property --border-gradient-anim-cy-padding {
      syntax: "<length>";
      inherits: true;
      initial-value: ${gradientCenterPadding}px;
    }

    border: 1px solid transparent;
    background: conic-gradient(
      from var(--border-gradient-anim-angle) at
        calc(
          var(--border-gradient-anim-cx) +
            var(--border-gradient-anim-cx-padding)
        )
        calc(
          var(--border-gradient-anim-cy) +
            var(--border-gradient-anim-cy-padding)
        ),
      ${gradientStops.join(", ")}
    );
    background-clip: border-box;
    background-origin: border-box;
    mask:
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px)
        padding-box exclude,
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
    animation: border-gradient-anim ${duration} linear infinite;

    @keyframes border-gradient-anim {
      ${Object.entries(keyframes)
        .map(([key, value]) => `${key} { ${value} }`)
        .join("\n")}
    }
  `;
}

export const BorderGradientAnim = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  z-index: 1;
  pointer-events: none;

  ${({ theme }) => {
    const stop1 = primaryPalette(theme, "100");
    const stop2 = rgba(saturate(0.5, primaryPalette(theme, "1200")), 0);
    // eslint-disable-next-line no-restricted-properties -- DES-1058
    const stop3 = theme.colors.WHITE;
    const stop4 = saturate(0.5, primaryPalette(theme, "1200"));
    // eslint-disable-next-line no-restricted-properties -- DES-1058
    const stop5 = rgba(theme.colors.INDIGO1, 0.8);
    // eslint-disable-next-line no-restricted-properties -- DES-1058
    const stop6 = rgba(theme.colors.INDIGO1, 0.0);

    return generateBorderGradientAnimCss({
      height: 157,
      width: 800,
      gradientCenterPadding: 8,
      duration: "2.4s",
      gradientStops: [
        `${stop6} 0%`,
        `${stop6} 60%`,
        `${stop5} 70%`,
        `${stop4} 78%`,
        `${stop4} 80%`,
        `${stop3} 95.5%`,
        `${stop2} 96%`,
        `${stop1} 100%`,
      ],
    });
  }}
`;

export const glassButtonCss = css`
  border: none;
  border-radius: 8px;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => primaryPalette(theme, "300", true)} -10%,
    ${({ theme }) => primaryPalette(theme, "600", true)} 150%
  );
  box-shadow: ${({ theme }) => {
    const top = `0 2px 0 -1px ${primaryPalette(theme, "500")} inset`;
    const edge = `0 0 1px 0 ${primaryPalette(theme, "1100")} inset`;
    const cutout = `0 0 0 1px ${primaryPalette(theme, "100", true)}`;
    return [top, edge, cutout, shadows.s].join(", ");
  }};

  &:disabled:not([data-loading="true"]) {
    color: hsla(0 0% 40% / 0.9);
    opacity: 1 !important;
    filter: brightness(0.74) saturate(120%);

    & > * {
      filter: brightness(0.6) saturate(60%);
    }
  }

  &[data-loading="true"] {
    opacity: 1 !important;
    cursor: progress;
  }
`;
